<template lang="pug">
footer#footer(:class="{ 'service-main-footer': currentRoutePath === 'my-projects' && currentService }")
        .bq-ft-wrap
            .bq-ft-left
                .bq-ft-brand
                    router-link.bq-ft-logo(to="/") Skapi
                    span.bq-ft-version(translate="no") v{{ APP_VERSION }}
                .bq-ft-info
                    a.bq-ft-terms(href="/pp.html" target="_blank") Terms of service / Privacy policy
                    span.bq-ft-company {{ new Date().getFullYear() }} SKAPI INC. South Korea, BROADWAYINC PTE. LTD. Singapore.
            .bq-ft-right
                span.bq-ft-email support&#64;broadwayinc&#46;com
                .bq-ft-icons
                    //- The source repo leads the row: it is the one social link a
                    //- developer is actually looking for.
                    a.bq-ft-icon(href="https://github.com/broadwayinc/skapi-js" target="_blank" rel="noopener" aria-label="GitHub" title="GitHub")
                        span.bq-ft-svg(:style="iconStyle(iconGithub)" aria-hidden="true")
                    a.bq-ft-icon(href="http://www.tiktok.com/@skapi_api" target="_blank" aria-label="TikTok" title="TikTok")
                        span.bq-ft-svg(:style="iconStyle(iconTiktok)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.instagram.com/skapi_api" target="_blank" aria-label="Instagram" title="Instagram")
                        span.bq-ft-svg(:style="iconStyle(iconInstagram)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.youtube.com/@skapi_official" target="_blank" aria-label="YouTube" title="YouTube")
                        span.bq-ft-svg(:style="iconStyle(iconYoutube)" aria-hidden="true")
                    a.bq-ft-icon(href="https://x.com/skapi_api" target="_blank" aria-label="X" title="X")
                        span.bq-ft-svg(:style="iconStyle(iconX)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.linkedin.com/company/skapi-backend-api/" target="_blank" aria-label="LinkedIn" title="LinkedIn")
                        span.bq-ft-svg(:style="iconStyle(iconLinkedin)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.facebook.com/profile.php?id=61577236221327" target="_blank" aria-label="Facebook" title="Facebook")
                        span.bq-ft-svg(:style="iconStyle(iconFacebook)" aria-hidden="true")
                    a.bq-ft-icon(href="https://dev.to/skapi_api" target="_blank" aria-label="Dev.to" title="Dev.to")
                        span.bq-ft-svg(:style="iconStyle(iconDevto)" aria-hidden="true")
</template>

<script setup>
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { currentService } from "@/views/project-list";
import { version as APP_VERSION } from "../../package.json";

import iconGithub from "@/assets/img/landingpage/icon_github.svg";
import iconTiktok from "@/assets/img/landingpage/icon_tiktok.svg";
import iconInstagram from "@/assets/img/landingpage/icon_instagram.svg";
import iconYoutube from "@/assets/img/landingpage/icon_youtube.svg";
import iconX from "@/assets/img/landingpage/icon_x.svg";
import iconLinkedin from "@/assets/img/landingpage/icon_linkedin.svg";
import iconFacebook from "@/assets/img/landingpage/icon_facebook.svg";
import iconDevto from "@/assets/img/landingpage/icon_devto.svg";

// Hands an icon file to .bq-ft-svg as its mask (see the style block).
const iconStyle = (url) => ({ "--bq-ft-icon": `url("${url}")` });

const route = useRoute();
let currentRoutePath = ref(route.path);

watch(
    () => route.path,
    (nv) => {
        let splitPath = nv.split("/");

        currentRoutePath.value = splitPath.length > 2 ? splitPath[1] : "";
    },
    { immediate: true }
);
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

#footer {
    position: relative;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.85rem;
    border-top: 1px solid @line;
    z-index: 999;

    a {
        color: @ink;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    a.bq-ft-logo {
        text-decoration: none;

        &:hover {
            text-decoration: none;
        }
    }
}

.bq-ft-logo {
    display: block;
    font-weight: 700;
    font-size: 1.05rem;
    letter-spacing: 0.04em;
    color: @ink;
    text-decoration: none;
    margin-bottom: 1rem;
}

.bq-ft-left {
    display: flex;
    flex-wrap: wrap;
    // gap: 2rem;
    flex: 1;
    min-width: 0;
}

.bq-ft-right {
    text-align: right;
}

.bq-ft-email {
    display: block;
    margin: 0 0 0.25rem;
    font-size: 0.85rem;
    color: @muted;
}

.bq-ft-terms {
    display: block;
    font-size: 0.8rem;
    color: @muted;
    cursor: pointer;

    &:hover {
        color: @ink;
    }
}

.bq-ft-company {
    font-size: 0.75rem;
    color: @muted;
}

.bq-ft-brand {
    display: flex;
    flex-direction: column;
    margin-right: 2rem;

    // Tighter than the default logo margin so the version sits snug beneath it.
    .bq-ft-logo {
        margin-bottom: 0.15rem;
    }
}

.bq-ft-version {
    font-size: 0.7rem;
    color: @muted;
    letter-spacing: 0.03em;
    opacity: 0.75;
}

.bq-ft-icons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.bq-ft-icon {
    display: inline-block;

    &:hover .bq-ft-svg {
        background-color: @pink;
        opacity: 1;
    }
}

// Each icon file is a mask over a token colour (ink at rest, brand blue on
// hover), so both states follow the scheme in either theme; the file's own
// black never shows. (A filter chain cannot read a token, and the old one
// was tuned to the light-mode blue only.) The link carries the name.
.bq-ft-svg {
    display: block;
    width: 1.75rem;
    height: 1.75rem;
    background-color: @ink;
    -webkit-mask: var(--bq-ft-icon) center / contain no-repeat;
    mask: var(--bq-ft-icon) center / contain no-repeat;
    opacity: 0.8;
    transition: background-color 0.15s, opacity 0.15s;
}

.bq-ft-wrap {
    padding: 1.275rem 2rem;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;

    .bq-ft-right {
        text-align: right;
    }
}

.bq-ft-info {
    .bq-ft-terms {
        margin-bottom: 0.25rem;
    }

    .bq-ft-company {
        display: block;
    }
}

/* ─── responsive ─── */
@media (max-width: 800px) {
    /* The footer does not render on mobile at all - the hamburger menu's own
       footer (navBar's .bq-mo-footer) carries the terms/social links there. Every
       other rule this media query used to hold styled DESCENDANTS of this hidden
       element (column layouts, the 1.25rem mobile gutter, the service-page 2px
       padding tweak) and so could never apply; they were leftovers from before
       the footer was hidden. If the mobile footer ever comes back, rebuild from
       the desktop rules plus the 1.25rem gutter the mobile bar uses. */
    #footer {
        display: none;
    }
}
</style>
